import 'react-native-get-random-values';
import 'react-native-url-polyfill/auto';
import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { useFonts } from 'expo-font';
import { Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter';
import { Merriweather_400Regular } from '@expo-google-fonts/merriweather';
import { Ionicons } from '@expo/vector-icons';
import { Stack } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useEffect } from 'react';
import 'react-native-reanimated';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { Platform, Linking, InteractionManager, AppState } from 'react-native';
import { supabase } from '@/lib/supabase';
import { useRouter } from 'expo-router';

import { useColorScheme } from '@/hooks/useColorScheme';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { NotesProvider } from '@/contexts/NotesContext';
import { BillProvider } from '@/contexts/BillContext';
import { SobrietyProvider } from '@/contexts/SobrietyProvider';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { LanguageProvider } from '@/contexts/LanguageContext';

import AuthGuard from '@/components/AuthGuard';
import StartupPromptQueue from '@/components/StartupPromptQueue';
import SafeProvider from '@/components/SafeProvider';
import { StartupPromptProvider } from '@/contexts/StartupPromptContext';
import { SentimentoProvider } from '@/contexts/SentimentoContext';
import { OnboardingProgressProvider } from '@/contexts/OnboardingProgressContext';
import { AudioPlayerProvider } from '@/contexts/AudioPlayerContext';
import { usePathname, useSegments } from 'expo-router';
import { useRef } from 'react';

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

// CRASH-SAFE Global error handler for uncaught exceptions
const handleGlobalError = (error: any, isFatal?: boolean) => {
  try {
    console.error('🚨 Global Error Handler:', {
      error: error?.message || 'Unknown error',
      stack: error?.stack,
      isFatal,
      timestamp: new Date().toISOString(),
    });

    // Don't crash the app for non-fatal errors
    if (!isFatal) {
      console.log('📝 Non-fatal error handled gracefully');
    }
  } catch (handlerError) {
    // Fail silently to prevent error handler crashes
    console.log('⚠️ Error handler failed safely');
  }
};

// CRASH-SAFE Global handler for unhandled promise rejections
const handleUnhandledRejection = (event: any) => {
  try {
    console.error('🚨 Unhandled Promise Rejection:', {
      reason: event?.reason?.message || event?.reason || 'Unknown reason',
      stack: event?.reason?.stack,
      timestamp: new Date().toISOString(),
    });

    // Prevent the default behavior (which would crash the app)
    event.preventDefault?.();

    // P05 Phase 2 (PV2-23): emit a rate-limited Clarity event for fetch rejections.
    // No HEAD probes fired — eliminates read-amplification and LLM-provider signal leak.
    const reason = event?.reason;
    const isFetchError =
      reason instanceof Error &&
      (reason.message?.includes('fetch') || reason.message?.includes('network'));

    if (isFetchError) {
      const now = Date.now();
      if (now - _lastNetworkTelemetryAt >= NETWORK_TELEMETRY_COOLDOWN_MS) {
        _lastNetworkTelemetryAt = now;
        import('@/utils/clarityEvents')
          .then(({ logClarityEvent }) => {
            logClarityEvent('network_rejection_detected', {
              message: reason.message?.substring(0, 120) ?? 'unknown',
            });
          })
          .catch(() => {});
      }
    }
  } catch (handlerError) {
    // Fail silently to prevent rejection handler crashes
    console.log('⚠️ Rejection handler failed safely');
  }
};

// P05 Phase 2 (PV2-23): rate-limit guard — at most one telemetry event per 30 s.
// Replaces checkNetworkHealth() HEAD probes that amplified every fetch rejection
// into three outbound requests to Supabase, Anthropic, and OneSignal.
let _lastNetworkTelemetryAt = 0;
const NETWORK_TELEMETRY_COOLDOWN_MS = 30_000;

// CRASH-SAFE Set up global error handlers
try {
  if (typeof ErrorUtils !== 'undefined') {
    ErrorUtils.setGlobalHandler(handleGlobalError);
  }
} catch (error) {
  console.log('⚠️ Global error handler setup failed safely');
}

// CRASH-SAFE Add unhandled rejection handler for web and React Native
try {
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    // Web environment
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
  } else if (typeof global !== 'undefined' && typeof process !== 'undefined') {
    // React Native global handler - use process.on for Node.js-like environment
    if (typeof process.on === 'function') {
      process.on('unhandledRejection', (error: any) => {
        handleUnhandledRejection({ reason: error });
      });
    }
  }
} catch (error) {
  console.log('⚠️ Rejection handler setup failed safely');
}

function ClarityScreenTracker() {
  const pathname = usePathname();
  const segments = useSegments();
  const previousPathname = useRef<string>('');

  useEffect(() => {
    if (pathname && pathname !== previousPathname.current) {
      previousPathname.current = pathname;
      
      const trackScreen = async () => {
        try {
          // The native SDK exposes a single "current page" hook
          // (setCurrentScreenName), so we set ONLY the friendly label —
          // calling trackScreen(rawPath) here as well would just overwrite
          // it. The route → label map (with humanised fallback) lives in
          // utils/clarityEvents.ts → pageNameForRoute.
          const { setClarityPageName, pageNameForRoute } = await import('@/utils/clarityEvents');
          await setClarityPageName(pageNameForRoute(pathname));
        } catch (error) {
          console.log('📊 Clarity: Screen tracking skipped (non-fatal)');
        }
      };

      trackScreen();
    }
  }, [pathname, segments]);

  return null;
}

/**
 * Telemedicine Phase 1 · P1.CLI.2 — deep-link handler for /meet/<channel>?token=…
 *
 * iOS Universal Links + Android App Links route mobile.padrinho.app/meet/* to
 * the app (via app.config.js → ios.associatedDomains and android.intentFilters).
 * Desktop browsers fall through to public/meet.html on the server.
 *
 * Cold-start: Linking.getInitialURL() returns the launch URL exactly once.
 * Warm-start: Linking.addEventListener('url', …) fires when the OS hands a
 *             new URL to the already-running app.
 *
 * We intentionally do NOT touch the Supabase session here — the linkToken
 * itself authorizes the join, and reaching for getSession() during cold start
 * is the documented deadlock path (see replit.md, Phase 0 lessons).
 *
 * We also intentionally do not validate the token client-side beyond a basic
 * shape check. The server is the only authority; the call screen surfaces a
 * clean error if /api/video/token rejects.
 */
function parseMeetDeepLink(rawUrl: string | null): { channel: string; token: string } | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    const url = new URL(rawUrl);
    const scheme = url.protocol.replace(/:$/, '').toLowerCase();

    // Channel is restricted to safe identifier chars (alphanumeric, dash,
    // underscore — same character class the appointment authorizer derives via
    // `appt-<uuid>`). Anything outside this range is either a typo or a
    // hand-crafted phishing link; we drop it on the floor rather than handing
    // it to the call screen.
    let channel: string | null = null;

    if (scheme === 'https') {
      // iOS Universal Link / Android App Link (native Mail, Messages, etc.):
      //   https://mobile.padrinho.app/meet/<channel>?token=…
      if (url.hostname !== 'mobile.padrinho.app') return null;
      const m = url.pathname.match(/^\/meet\/([A-Za-z0-9_-]+)\/?$/);
      if (!m || !m[1]) return null;
      channel = m[1];
    } else if (scheme === 'padrinhoelo') {
      // Custom-scheme fallback used by public/meet.html when the link is opened
      // inside an in-app browser (Gmail / Outlook / WhatsApp), which silently
      // bypass Universal/App Links. meet.html auto-tries this scheme on load
      // and falls back to the web room if the app isn't installed. WHATWG URL
      // parses the first segment after `//` as the host for custom schemes, so
      // accept both shapes:
      //   padrinhoelo://meet/<channel>?token=…   → host "meet", path /<channel>
      //   padrinhoelo:///meet/<channel>?token=…  → host "",     path /meet/<channel>
      if (url.hostname === 'meet') {
        const m = url.pathname.match(/^\/([A-Za-z0-9_-]+)\/?$/);
        if (!m || !m[1]) return null;
        channel = m[1];
      } else if (!url.hostname) {
        const m = url.pathname.match(/^\/meet\/([A-Za-z0-9_-]+)\/?$/);
        if (!m || !m[1]) return null;
        channel = m[1];
      } else {
        return null;
      }
    } else {
      return null;
    }

    channel = decodeURIComponent(channel);
    const token = url.searchParams.get('token');
    if (!channel || !token) return null;
    return { channel, token };
  } catch {
    return null;
  }
}

function useMeetDeepLinks() {
  const router = useRouter();
  useEffect(() => {
    let cancelled = false;
    const route = (rawUrl: string | null) => {
      const parsed = parseMeetDeepLink(rawUrl);
      if (!parsed || cancelled) return;
      console.log('🔗 [meet-deeplink] routing to call screen', { channel: parsed.channel });
      // Fire-and-forget: telemedicine deep-link surface metric. Channel is a
      // server-derived identifier (appt-<uuid>), not PII.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('meeting_deeplink_opened', { channel: parsed.channel })
        )
        .catch(() => {});
      // sessionType defaults to 'appointment' inside the call screen; sessionId
      // is recovered from the token's `sid` claim on screen.
      router.push({
        pathname: '/video-call/[channel]',
        params: {
          channel: parsed.channel,
          linkToken: parsed.token,
          sessionType: 'appointment',
        },
      });
    };

    // Cold start
    Linking.getInitialURL()
      .then(route)
      .catch((e) => console.warn('⚠️ [meet-deeplink] getInitialURL failed:', e?.message || e));

    // Warm start
    const sub = Linking.addEventListener('url', (event) => route(event?.url ?? null));

    return () => {
      cancelled = true;
      sub.remove();
    };
  }, [router]);
}

function RootLayout() {
  const colorScheme = useColorScheme();
  useMeetDeepLinks();
  
  const [essentialFontsLoaded, essentialFontError] = useFonts({
    'Sentient-Regular': require('../assets/fonts/Sentient-Regular.otf'),
    'Sentient-Italic': require('../assets/fonts/Sentient-Italic.otf'),
    'InstrumentSerif_400Regular': require('../assets/fonts/InstrumentSerif-Regular.ttf'),
    'InstrumentSerif_400Regular_Italic': require('../assets/fonts/InstrumentSerif-Italic.ttf'),
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    ...Ionicons.font,
  });

  useEffect(() => {
    if (essentialFontError) {
      console.error('❌ Essential font loading error:', essentialFontError);
    }
    if (essentialFontsLoaded) {
      console.log('✅ Essential fonts loaded (Sentient, InstrumentSerif, Inter weights)', {
        platform: Platform.OS,
      });
      
      // P05 Phase 3 (PV2-25): defer secondary font I/O until after first paint
      // so it no longer competes with tier-1 SDK initialisers for network/CPU at boot.
      InteractionManager.runAfterInteractions(() => {
        Promise.all([
          import('expo-font'),
          import('@expo-google-fonts/instrument-sans'),
        ]).then(([{ loadAsync }, instrumentSans]) => {
          loadAsync({
            SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
            'Sentient-Bold': require('../assets/fonts/Sentient-Bold.otf'),
            'Sentient-Medium': require('../assets/fonts/Sentient-Medium.otf'),
            'Inter': require('../assets/fonts/Inter-VariableFont_opsz,wght.ttf'),
            'Inter-Italic': require('../assets/fonts/Inter-Italic-VariableFont_opsz,wght.ttf'),
            Merriweather_400Regular,
            InstrumentSans_400Regular: instrumentSans.InstrumentSans_400Regular,
            InstrumentSans_400Regular_Italic: instrumentSans.InstrumentSans_400Regular_Italic,
            InstrumentSans_500Medium: instrumentSans.InstrumentSans_500Medium,
            InstrumentSans_600SemiBold: instrumentSans.InstrumentSans_600SemiBold,
            InstrumentSans_700Bold: instrumentSans.InstrumentSans_700Bold,
          }).catch(() => {
            // Non-blocking — app functions correctly with essential fonts.
          });
        });
      });
    }
  }, [essentialFontsLoaded, essentialFontError]);
  
  // On web there is no native splash and no pre-rendered HTML to mask a stalled
  // font load, so gating the first paint on fonts would leave the page blank
  // until every font resolves (and permanently blank if one stalls/fails).
  // Render immediately on web — custom fonts swap in via the browser's normal
  // font-loading once ready. Native keeps the original font-gated launch.
  const loaded = Platform.OS === 'web' ? true : essentialFontsLoaded;

  // CRASH-SAFE Badge Manager initialization
  const initializeBadgeManager = async () => {
    try {
      console.log('🏷️ Initializing Badge Manager...');
      const BadgeManagerService = await import('@/services/badgeManagerService');
      await BadgeManagerService.default.initialize(true);
      console.log('✅ Badge Manager Service initialized');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn('⚠️ Badge Manager initialization failed safely:', errorMessage);
      console.log('🏷️ App will continue to work without badge management');
    }
  };

  // CRASH-SAFE Meeting Reminder System initialization
  const initializeMeetingReminders = async () => {
    try {
      console.log('🚀 Initializing meeting reminder system from root layout...');
      const meetingReminderInitializer = await import('@/services/meetingReminderInitializer');
      await meetingReminderInitializer.default.initialize();
      console.log('✅ Meeting reminder system initialized successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Meeting reminder initialization failed safely:', errorMessage);
      console.log('📅 App will continue to work without meeting reminders');
    }
  };

  // CRASH-SAFE Image preloading
  const preloadImages = async () => {
    try {
      console.log('🖼️ Preloading empty state images...');
      const { preloadEmptyStateImages } = await import('@/components/CachedImage');
      await preloadEmptyStateImages();
      console.log('✅ Images preloaded successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn('⚠️ Image preloading failed safely:', errorMessage);
      console.log('🖼️ App will continue to work without preloaded images');
    }
  };

  // CRASH-SAFE Clarity Analytics initialization with ATT permission check
  const initializeClarity = async (hasTrackingPermission: boolean) => {
    try {
      console.log('📊 Initializing Clarity Analytics...', { hasTrackingPermission, platform: Platform.OS });
      const ClarityService = await import('@/services/clarityService');
      await ClarityService.default.getInstance().initialize(hasTrackingPermission);
      console.log('✅ Clarity Analytics initialized successfully');

      // PRD §4 — `app_opened` macro event. Idempotent across the same cold
      // start; fires once per JS bundle load. is_returning_user persists
      // across cold starts via AsyncStorage.
      try {
        const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
        const APP_OPENED_FLAG = '@padrinho/clarity/has_opened_before';
        const seen = await AsyncStorage.getItem(APP_OPENED_FLAG);
        const isReturningUser = !!seen;
        if (!seen) {
          await AsyncStorage.setItem(APP_OPENED_FLAG, '1');
        }
        const { logAppOpened } = await import('@/utils/clarityEvents');
        logAppOpened({
          is_returning_user: isReturningUser,
          start_type: 'cold',
        }).catch(() => {});
      } catch {
        // Non-blocking — funnel just loses one data point.
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn('⚠️ Clarity initialization failed safely:', errorMessage);
      console.log('📊 App will continue to work without Clarity analytics');
    }
  };

  // CRASH-SAFE RevenueCat initialization (disabled for Android in service)
  const initializeRevenueCat = async () => {
    try {
      console.log('💰 Initializing RevenueCat SDK...');
      const RevenueCatService = await import('@/services/revenueCatService');
      await RevenueCatService.default.initialize();
      console.log('✅ RevenueCat SDK initialized successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn('⚠️ RevenueCat initialization failed safely:', errorMessage);
      console.log('💰 App will continue to work without in-app purchases');
    }
  };

  // CRASH-SAFE App icon service initialization. Registers the global
  // RevenueCat customer-info-updated listener so the iOS alternate icon
  // (Plus) tracks subscription status from any screen — not only when the
  // user happens to be on the meeting detail screen, which used to be the
  // only place that mounted the icon-update side effect.
  const initializeAppIconService = async () => {
    try {
      const { appIconService } = await import('@/services/appIconService');
      await appIconService.initialize();
      console.log('✅ App icon service initialized');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn('⚠️ App icon service initialization failed safely:', errorMessage);
    }
  };

  // CRASH-SAFE OneSignal silent SDK initialization.
  // Registers the device with OneSignal on every app open without showing any dialog.
  // Also re-links the authenticated user's external_id on every open — this is the
  // catch-all that recovers users whose initial login() call failed (race condition
  // between SIGNED_IN event and OneSignal initialization completing).
  // The permission dialog is shown separately in onboarding-notifications.tsx via requestPermission().
  const initializeOneSignal = async () => {
    try {
      console.log('🔔 Initializing OneSignal SDK (silent)...');
      const oneSignalService = await import('@/services/oneSignalService');
      await oneSignalService.default.initialize();
      console.log('✅ OneSignal SDK initialized (silent)');

      // After SDK is ready, re-link the current user's external_id.
      // This catches users whose initial link failed (e.g. SDK was still initializing
      // when SIGNED_IN fired during signup). Runs on every app open — safe and idempotent.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          console.log('🔗 [OneSignal] Re-linking user external_id on app open...');
          await oneSignalService.default.setExternalUserId(session.user.id);
          console.log('✅ [OneSignal] User external_id linked on app open');
        }
      } catch (linkError: unknown) {
        const msg = linkError instanceof Error ? linkError.message : 'Unknown error';
        console.warn('⚠️ [OneSignal] Could not re-link user on app open (non-fatal):', msg);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn('⚠️ OneSignal initialization failed safely:', errorMessage);
      console.log('🔔 App will continue to work without push notification registration');
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      if (!loaded) return;

      let hasTrackingPermission = true; // Default to true for Android

      // Request ATT permission on iOS only (if native module is available).
      // Facebook SDK and Google Analytics are deferred to tier-2 below so they
      // no longer block the critical path — they receive hasTrackingPermission
      // via closure capture after ATT resolves.
      if (Platform.OS === 'ios') {
        try {
          console.log('🍎 Requesting App Tracking Transparency permission...');
          const TrackingTransparency = await import('expo-tracking-transparency');
          const { status } = await TrackingTransparency.requestTrackingPermissionsAsync();
          hasTrackingPermission = status === 'granted';
          console.log('🍎 ATT Permission:', status, '| Tracking allowed:', hasTrackingPermission);
        } catch (error) {
          console.warn('⚠️ ATT module not available (requires native build):', error instanceof Error ? error.message : 'Unknown error');
          hasTrackingPermission = false;
        }
      }

      // Hide splash screen — UI is visible from this point.
      await SplashScreen.hideAsync();

      // P05 Phase 1 (PV2-13): tiered SDK init per PP-14.
      //
      // Tier 1 — Essential. Runs immediately after splash-hide.
      // BadgeManager: sets the notification badge count on first launch.
      // OneSignal: silent register must happen before any push permission prompt.
      await Promise.allSettled([
        initializeBadgeManager(),
        initializeOneSignal(),
        // PRD_GOOGLE_AUTH §2 — configure the Google Sign-In SDK so the welcome
        // screen button can open the native account picker. No-ops on web and
        // when the Web Client ID env var is missing.
        import('./../services/googleAuthService')
          .then(({ googleAuthService }) => googleAuthService.configure())
          .catch((error) => {
            console.warn('⚠️ Google Sign-In configure failed (non-fatal):', error instanceof Error ? error.message : 'Unknown error');
          }),
      ]);

      // PUSH RECOVERY (June 2026): One-time auto-request for users whose
      // OS permission dialog was never shown during the broken SDK period
      // (31/05–17/06). Fires 3 s after app stabilises, once per device.
      //   - Not determined → OS dialog appears, user can accept or deny
      //   - Denied          → silent no-op (we respect the user's choice)
      //   - Granted         → silent no-op (INITIAL_SESSION fix handles linking)
      setTimeout(async () => {
        try {
          const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
          const done = await AsyncStorage.getItem('@padrinho:pushAutoRequested_v1');
          if (done) return;
          const { data: { session: s } } = await supabase.auth.getSession();
          if (!s?.user?.id) return;
          // Mark before calling so a crash never causes a repeated dialog.
          await AsyncStorage.setItem('@padrinho:pushAutoRequested_v1', '1');
          const oneSignalSvc = await import('@/services/oneSignalService');
          await oneSignalSvc.default.requestPermission();
          console.log('🔔 [PushRecovery] One-time permission request completed');
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'Unknown error';
          console.warn('⚠️ [PushRecovery] Auto-request failed (non-fatal):', msg);
        }
      }, 3000);

      // Tier 2 — Idle. Runs after first paint via InteractionManager.
      // All tier-2 SDKs do network I/O; deferring 2–3 s has no UX impact.
      // Facebook SDK: tier-2 here rather than true tier-3 (PRD open decision)
      // because applyTrackingPosture must pair with initialize() and both need
      // the ATT outcome. Full lazy-on-event deferral is a future FB-service task.
      InteractionManager.runAfterInteractions(() => {
        import('./../services/facebookService')
          .then(async ({ FacebookService }) => {
            await FacebookService.initialize();
            await FacebookService.applyTrackingPosture(hasTrackingPermission);
          })
          .catch((error) => {
            console.warn('⚠️ Facebook SDK init failed (non-fatal):', error instanceof Error ? error.message : 'Unknown error');
          });

        // Google Analytics: initialize + apply posture together in tier-2.
        // Granted → collection ON. Denied → collection OFF.
        import('./../services/googleAnalyticsService')
          .then(async ({ GoogleAnalyticsService }) => {
            await GoogleAnalyticsService.initialize();
            await GoogleAnalyticsService.setTrackingPosture(hasTrackingPermission);
          })
          .catch((error) => {
            console.warn('⚠️ Google Analytics init failed (non-fatal):', error instanceof Error ? error.message : 'Unknown error');
          });

        Promise.allSettled([
          initializeMeetingReminders(),
          preloadImages(),
          initializeClarity(hasTrackingPermission),
          initializeRevenueCat(),
          initializeAppIconService(),
        ]);
      });
    };

    initializeApp();
  }, [loaded]);

  // CRASH-SAFE Auth state listeners
  useEffect(() => {
    let subscription: any;
    
    try {
      const { data } = supabase.auth.onAuthStateChange(async (event: any, session: any) => {
        try {
          console.log('🔐 [RootLayout] Auth state changed:', event);

          if (event === 'SIGNED_OUT') {
            try {
              // Clear badge when user signs out
              const BadgeManagerService = await import('@/services/badgeManagerService');
              await BadgeManagerService.default.clearBadge();
              console.log('🏷️ [RootLayout] Badge cleared on sign out');

              // Clear all notification caches on sign out
              const NotificationCacheService = await import('@/services/notificationCacheService');
              await NotificationCacheService.default.forceGlobalRefresh();
              console.log('🗑️ [RootLayout] All caches cleared on sign out');

              // Log out from RevenueCat (Android disabled in service)
              const RevenueCatService = await import('@/services/revenueCatService');
              await RevenueCatService.default.logoutUser();
              console.log('💰 [RootLayout] RevenueCat user logged out');
            } catch (error: any) {
              console.error('❌ [RootLayout] Error clearing data on sign out (non-fatal):', error);
            }
          }

          // Identify the user with RevenueCat on BOTH `SIGNED_IN` (fresh
          // login) and `INITIAL_SESSION` (warm app open with a cached
          // session). Without the INITIAL_SESSION branch the SDK keeps
          // serving the anonymous device user on warm launches, which
          // means a Plus account looks Free until the user manually
          // re-authenticates — the symptom that surfaces as the meet
          // detail page showing the paywall to a paying user.
          if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
            try {
              const RevenueCatService = await import('@/services/revenueCatService');
              await RevenueCatService.default.identifyUser(session.user.id);
              console.log(`💰 [RootLayout] RevenueCat user identified (${event})`);
            } catch (e: any) {
              console.warn('⚠️ [RootLayout] RevenueCat identify failed (non-fatal):', e?.message);
            }

            // Link the user with OneSignal on BOTH fresh logins and warm session
            // restores. Previously only SIGNED_IN triggered this — returning users
            // who open the app with a cached session fire INITIAL_SESSION instead,
            // so they were never linked and remained 404 in OneSignal.
            try {
              const oneSignalService = await import('@/services/oneSignalService');
              await oneSignalService.default.setExternalUserId(session.user.id);
              console.log(`🔔 [RootLayout] OneSignal user linked (${event})`);
            } catch (e: any) {
              console.warn('⚠️ [RootLayout] OneSignal link failed (non-fatal):', e?.message);
            }
          }

          if (event === 'SIGNED_IN' && session?.user) {
            try {
              const BadgeManagerService = await import('@/services/badgeManagerService');
              await BadgeManagerService.default.initialize(true);
              console.log('🏷️ [RootLayout] Badge initialized on sign in');
            } catch (e: any) {
              console.warn('⚠️ [RootLayout] Badge init failed (non-fatal):', e?.message);
            }

            try {
              const NotificationCacheService = await import('@/services/notificationCacheService');
              await NotificationCacheService.default.handleUserSwitch(session.user.id);
              console.log('🔄 [RootLayout] Cache context switch handled');
            } catch (e: any) {
              console.warn('⚠️ [RootLayout] Cache context switch failed (non-fatal):', e?.message);
            }

            try {
              const { storeReviewService } = await import('@/services/storeReviewService');
              await storeReviewService.recordFirstOpen();
              const reviewPrompted = await storeReviewService.checkAndPromptReview();
              if (reviewPrompted) {
                console.log('⭐ [RootLayout] Store review prompt shown');
              }
            } catch (e: any) {
              console.warn('⚠️ [RootLayout] Store review failed (non-fatal):', e?.message);
            }

            // GA `login` is fired here ONLY for true session-restore (i.e. an
            // existing session is already valid at app cold-start). Explicit
            // user logins go through `app/login.tsx` → `logAuthLoginSucceeded`,
            // which fans out to GA via the helper. Both paths emit
            // SIGNED_IN, so the inline call is gated to INITIAL_SESSION to
            // avoid double-firing on every email/biometric login.
            //
            // Note: this branch sits inside `if (event === 'SIGNED_IN' && ...)`
            // for legacy reasons, but Supabase emits one of SIGNED_IN /
            // INITIAL_SESSION per session. The check below is a no-op on
            // SIGNED_IN — kept explicit for readability when this block
            // gets revisited.
            if (event === 'INITIAL_SESSION') {
              try {
                const { GoogleAnalyticsService } = await import('./../services/googleAnalyticsService');
                GoogleAnalyticsService.logLogin('session');
              } catch (e: any) {
                console.warn('⚠️ [RootLayout] Google Analytics logLogin failed (non-fatal):', e?.message);
              }
            }
          }

          if (event === 'TOKEN_REFRESHED') {
            try {
              // Invalidate cache on token refresh to ensure fresh data
              const { data: { user } } = await supabase.auth.getUser();
              if (user) {
                const NotificationCacheService = await import('@/services/notificationCacheService');
                await NotificationCacheService.default.invalidateCache(user.id);
                console.log('🔄 [RootLayout] Cache invalidated on token refresh');
              }
            } catch (error: any) {
              console.error('❌ [RootLayout] Error invalidating cache on token refresh (non-fatal):', error);
            }
          }
        } catch (stateError) {
          console.error('❌ [RootLayout] Auth state change handler failed safely:', stateError);
        }
      });
      
      subscription = data.subscription;
    } catch (authError) {
      console.error('❌ [RootLayout] Auth listener setup failed safely:', authError);
    }

    return () => {
      try {
        subscription?.unsubscribe?.();
      } catch (error) {
        console.log('⚠️ Auth subscription cleanup failed safely');
      }
    };
  }, []);

  // When the user returns from iOS/Android Settings having enabled push
  // notifications, re-link their token with OneSignal immediately so they
  // start receiving pushes without needing to kill and reopen the app.
  // Uses a synchronous permission check so it's fast on every foreground
  // transition and only calls OneSignal.login() when permission is on.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', async (nextState) => {
      if (nextState !== 'active') return;
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!s?.user?.id) return;
        const oneSignalSvc = await import('@/services/oneSignalService');
        await oneSignalSvc.default.checkAndRelinkIfGranted(s.user.id);
      } catch {
        // non-fatal
      }
    });
    return () => subscription.remove();
  }, []);

  if (!loaded) {
    return null;
  }

  return (
    <SafeAreaProvider>
      <LanguageProvider>
    <GestureHandlerRootView style={{ flex: 1 }}>
      <BottomSheetModalProvider>
        <ErrorBoundary>
          <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
            <SafeProvider name="AuthProvider">
              <AuthProvider>
                <StartupPromptProvider>
                <SafeProvider name="NotificationProvider">
                  <NotificationProvider>
                    <SafeProvider name="NotesProvider">
                      <NotesProvider>
                        <SafeProvider name="BillProvider">
                          <BillProvider>
                            <SafeProvider name="SobrietyProvider">
                              <SobrietyProvider>
                                <SafeProvider name="SentimentoProvider">
                                  <SentimentoProvider>
                                    <SafeProvider name="OnboardingProgressProvider">
                                      <OnboardingProgressProvider>
                                        <SafeProvider name="AudioPlayerProvider">
                                          <AudioPlayerProvider>
                                            <SafeProvider name="AuthGuard">
                                              <AuthGuard>
                                                <StartupPromptQueue />
                                                <ClarityScreenTracker />
                                            <Stack screenOptions={{ 
                                              headerShown: false,
                                              animation: 'slide_from_right',
                                            }}>
                                          <Stack.Screen name="index" options={{ headerShown: false, animation: 'fade' }} />
                                          <Stack.Screen name="(tabs)" options={{ headerShown: false, animation: 'none' }} />
                                          <Stack.Screen name="welcome" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="login" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="forgot-password" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="first-access" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="reset-password" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="perfil" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="diary" options={{ headerShown: false, animation: 'none' }} />
                                          <Stack.Screen
                                            name="add-note"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom'
                                            }}
                                          />
                                          <Stack.Screen
                                            name="community-new-post"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom'
                                            }}
                                          />
                                          <Stack.Screen name="sobriety-clock" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="sobriety-input" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="sobriety-relapse" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="sobriety-congratulations" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen 
                                            name="share-sobriety" 
                                            options={{ 
                                              headerShown: false, 
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom'
                                            }} 
                                          />
                                          <Stack.Screen
                                            name="notifications"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom'
                                            }}
                                          />
                                          <Stack.Screen
                                            name="menu"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom'
                                            }}
                                          />
                                          <Stack.Screen
                                            name="audio-player"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom'
                                            }}
                                          />
                                          <Stack.Screen name="subscription" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen
                                            name="diario-sentimento"
                                            options={{
                                              headerShown: false,
                                              animation: 'slide_from_right'
                                            }}
                                          />
                                          <Stack.Screen
                                            name="diario-sentimento-categoria"
                                            options={{
                                              headerShown: false,
                                              animation: 'slide_from_right'
                                            }}
                                          />
                                          <Stack.Screen
                                            name="diario-sentimento-nota"
                                            options={{
                                              headerShown: false,
                                              animation: 'slide_from_right'
                                            }}
                                          />
                                          <Stack.Screen name="encontros-online" options={{ headerShown: false, animation: 'none' }} />
                                          <Stack.Screen name="agendamentos" options={{ headerShown: false, animation: 'none' }} />
                                          <Stack.Screen name="community" options={{ headerShown: false, animation: 'none' }} />
                                          <Stack.Screen
                                            name="community-post"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom',
                                              headerShown: false
                                            }}
                                          />
                                          <Stack.Screen
                                            name="community-profile"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom',
                                              headerShown: false
                                            }}
                                          />
                                          <Stack.Screen name="trail" options={{ headerShown: false, animation: 'none' }} />
                                          <Stack.Screen
                                            name="meeting/[id]"
                                            options={{
                                              presentation: 'modal',
                                              animation: 'slide_from_bottom',
                                              headerShown: false
                                            }}
                                          />
                                          <Stack.Screen name="webview" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="change-email" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="change-password" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="change-phone" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="change-name" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="change-username" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="change-goal" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="delete-account" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="delete-account-confirmation" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="notification-settings" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="interests-settings" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="provider" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="csat" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="language-settings" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-notifications" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="email-success" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-welcome" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-name" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-interests" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-sponsorship" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-clinic" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-gender" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-support-recipient" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="verify-code" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="set-password" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="onboarding-success" options={{ headerShown: false, animation: 'slide_from_right' }} />
                                          <Stack.Screen name="+not-found" />
                                            </Stack>
                                              </AuthGuard>
                                            </SafeProvider>
                                          </AudioPlayerProvider>
                                        </SafeProvider>
                                      </OnboardingProgressProvider>
                                    </SafeProvider>
                                  </SentimentoProvider>
                                </SafeProvider>
                              </SobrietyProvider>
                            </SafeProvider>
                          </BillProvider>
                        </SafeProvider>
                      </NotesProvider>
                    </SafeProvider>
                  </NotificationProvider>
                </SafeProvider>
                </StartupPromptProvider>
              </AuthProvider>
            </SafeProvider>
            <StatusBar style="auto" />
          </ThemeProvider>
        </ErrorBoundary>
      </BottomSheetModalProvider>
    </GestureHandlerRootView>
      </LanguageProvider>
    </SafeAreaProvider>
  );
}

export default RootLayout;